// Serialised preference writes (WP-SHELL; WP-TRUST's request 7 of 12 Oct). The server stores each `pref:<key>` as a versioned layout row,
// so two PUTs of one key in flight race on that version and the second answers 409: the screen then showed the second value while the
// server kept the first. This writer sends one PUT per key at a time; values written meanwhile wait, and only the LATEST is sent when the
// running PUT settles. A 409 that still happens (another tab or device wrote the key) is retried once with the latest value.
// Pure (no React), so it is unit-tested: writer.test.mjs.

export interface PrefWriterOptions {
  /** PUT one key (resolves on 2xx, rejects with the API error). */
  put: (key: string, value: unknown) => Promise<unknown>;
  /** The newest value this device wants for the key; `has: false` when nothing waits. */
  wanted: (key: string) => { has: boolean; value?: unknown };
  /** The server now holds `value`, which is still the newest wanted value: the caller may stop protecting the key. */
  done: (key: string, value: unknown) => void;
  /** The write failed for good (offline, refused, a second 409): the caller keeps the local value. */
  failed?: (key: string, error: unknown) => void;
  /** A conflict answer (HTTP 409). */
  isConflict: (error: unknown) => boolean;
}

export interface PrefWriter {
  /** Send the newest wanted value of `key` (now, or after the PUT in flight settles). */
  flush: (key: string) => void;
  /** Forget the keys in flight (a different person signed in). */
  reset: () => void;
  /** Keys with a PUT in flight (for tests). */
  busy: () => string[];
}

export function createPrefWriter(o: PrefWriterOptions): PrefWriter {
  const inFlight = new Set<string>();
  let generation = 0;

  const flush = (key: string): void => {
    if (inFlight.has(key)) return;
    const w = o.wanted(key);
    if (!w.has) return;
    inFlight.add(key);
    const gen = generation;
    let sent = w.value;
    let failed = false;
    const put = (value: unknown, retried: boolean): Promise<unknown> =>
      o.put(key, value).catch((e: unknown) => {
        if (!retried && o.isConflict(e)) {
          const now = o.wanted(key);
          sent = now.has ? now.value : value;
          return put(sent, true);
        }
        throw e;
      });
    put(sent, false)
      .catch((e: unknown) => {
        failed = true;
        o.failed?.(key, e);
      })
      .finally(() => {
        if (gen !== generation) return;
        inFlight.delete(key);
        if (failed) return;
        const now = o.wanted(key);
        if (!now.has) return;
        if (Object.is(now.value, sent)) o.done(key, sent);
        else flush(key);
      });
  };

  return {
    flush,
    reset: () => {
      generation += 1;
      inFlight.clear();
    },
    busy: () => [...inFlight],
  };
}
